import { useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, EmptyState, ExplainerBanner, Input, OptionGrid } from '@/ds'
import { QUERY } from '@/lib/query-params'
import { isServiceError } from '@/services/errors'
import type { ServiceErrorCode } from '@/services/errors'
import { enlaceService } from '@/services/enlace'
import type { ResendChannel } from '@/services/enlace'
import { isMock } from '@/services/config'

interface Props {
  code: ServiceErrorCode
  guestCode: string
}

/**
 * Enlace caducado o inválido (prompt §6 y §11.6). El bundle no trae pantalla propia: se compone con
 * los textos de la app actual (v2.legacy.error / guestCodeForm) y los del check-in del bundle
 * (canal de envío, Número de WhatsApp, Correo electrónico, Reenviar). El reenvío corre contra mock
 * hasta que exista POST /guest-links/resend (docs/backend/enlace.md).
 */
export function LinkExpired({ code, guestCode }: Props) {
  const { t } = useTranslation()
  const [manualCode, setManualCode] = useState('')
  const [channel, setChannel] = useState<ResendChannel>('whatsapp')
  const [contact, setContact] = useState('')
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [unsupported, setUnsupported] = useState(false)
  const resendAvailable = isMock('enlace')

  const enter = (e: FormEvent) => {
    e.preventDefault()
    const params = new URLSearchParams(window.location.search)
    params.set(QUERY.guestCode, manualCode.trim())
    window.location.assign(`/?${params.toString()}`)
  }

  const resend = async (e: FormEvent) => {
    e.preventDefault()
    if (!contact.trim()) return
    setSending(true)
    try {
      await enlaceService.resend({ channel, contact: contact.trim() })
      setSent(true)
    } catch (err) {
      if (isServiceError(err) && err.code === 'not_supported') setUnsupported(true)
    } finally {
      setSending(false)
    }
  }

  const isServer = code === 'server'
  return (
    <main className="page">
      {isServer ? (
        <EmptyState tone="error" icon="x-circle" headingLevel={1} title={t('v2.legacy.error.anErrorHasOcurred')} actionLabel={t('v2.comun.reintentar')} onAction={() => window.location.reload()}>
          {t('v2.legacy.error.tryAgainOrContactUs')}
        </EmptyState>
      ) : (
        <EmptyState icon="lock" headingLevel={1} title={code === 'link_invalid' && !guestCode ? t('v2.legacy.error.noGuestCodeProvided.title') : t('v2.legacy.error.guestCodeNotFound')}>
          {code === 'link_invalid' && !guestCode ? t('v2.legacy.error.noGuestCodeProvided.description') : t('v2.legacy.error.tryAgainOrContactUs')}
        </EmptyState>
      )}

      <form onSubmit={enter} className="page__form">
        <h2>{t('v2.legacy.guestCodeForm.title')}</h2>
        <p className="page__lead">{t('v2.legacy.guestCodeForm.subtitle')}</p>
        <Input label={t('v2.legacy.labels.reserveCode')} placeholder={t('v2.legacy.guestCodeForm.placeholder')} value={manualCode} onChange={(e) => setManualCode(e.target.value)} autoComplete="off" />
        <Button type="submit" disabled={!manualCode.trim()}>
          {t('v2.legacy.guestCodeForm.enter')}
        </Button>
      </form>

      {resendAvailable && !unsupported && (
        <form onSubmit={(e) => void resend(e)} className="page__form">
          <OptionGrid
            label={t('v2.enlace.comoEnviamos')}
            value={channel}
            onChange={(id) => setChannel(id as ResendChannel)}
            options={[
              { id: 'whatsapp', label: 'WhatsApp' },
              { id: 'email', label: t('v2.ds.contact.email') },
            ]}
          />
          <Input
            label={channel === 'whatsapp' ? t('v2.enlace.numeroWhatsApp') : t('v2.enlace.correo')}
            type={channel === 'whatsapp' ? 'tel' : 'email'}
            inputMode={channel === 'whatsapp' ? 'tel' : 'email'}
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            autoComplete={channel === 'whatsapp' ? 'tel' : 'email'}
          />
          {sent ? (
            <ExplainerBanner tone="success" title={t('v2.legacy.home.Completed')}>
              {contact}
            </ExplainerBanner>
          ) : (
            <Button type="submit" variant="secondary" loading={sending} disabled={!contact.trim()}>
              {t('v2.enlace.reenviar')}
            </Button>
          )}
        </form>
      )}
    </main>
  )
}
